import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';

export default function SiparisDetay() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profil } = useAuth();
  const [siparis, setSiparis] = useState(null);
  const [kalemler, setKalemler] = useState([]);
  const [dekontlar, setDekontlar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [odemeModal, setOdemeModal] = useState(false);
  const [odemeForm, setOdemeForm] = useState({ tutar: '', odeme_turu: 'banka', not_: '' });
  const [odemeSekmesi, setOdemeSekmesi] = useState('temsilci_yukle');
  const [dekontDosya, setDekontDosya] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const dosyaRef = useRef();
  const [kargoModal, setKargoModal] = useState(false);
  const [takipSorguluyor, setTakipSorguluyor] = useState(false);
  const [kargoForm, setKargoForm] = useState({ kargo_kodu: '', elden_teslim: false, teslim_notu: '', kargo_firma_id: '' });
  const [kargoFirmalar, setKargoFirmalar] = useState([]);
  const [kargoLoglari, setKargoLoglari] = useState([]);

  // ─── ÜRÜN EKLEME STATE ───
  const [urunModal, setUrunModal] = useState(false);
  const [tumUrunler, setTumUrunler] = useState([]);
  const [stokMap, setStokMap] = useState({});
  const [urunArama, setUrunArama] = useState('');
  const [ekSepet, setEkSepet] = useState([]); // { urun, miktar, fiyat, komisyon }
  const [urunYukleniyor, setUrunYukleniyor] = useState(false);
  const [urunHata, setUrunHata] = useState('');

  useEffect(() => { fetchData(); }, [id]);

  async function fetchData() {
    const [{ data: s }, { data: k }, { data: d }, { data: kf }] = await Promise.all([
      supabase.from('siparisler')
        .select('*, musteriler(*), firmalar(ad), depolar(ad), kargo_firmalari(ad, kod)')
        .eq('id', id).single(),
      supabase.from('siparis_kalemleri')
        .select('*, urunler(ad, grup, alt_grup)')
        .eq('siparis_id', id),
      supabase.from('odeme_dekontlari')
        .select('*')
        .eq('siparis_id', id)
        .order('created_at', { ascending: false }),
      supabase.from('kargo_firmalari').select('*').eq('aktif', true),
    ]);
    setSiparis(s);
    setKalemler(k || []);
    setDekontlar(d || []);
    setKargoFirmalar(kf || []);
    const varsayilan = (kf || []).find(f => f.varsayilan);
    if (varsayilan) setKargoForm(f => ({ ...f, kargo_firma_id: varsayilan.id }));
    setLoading(false);
  }

  async function fetchKargoFirmalar() {
    const { data } = await supabase.from('kargo_firmalari').select('*').eq('aktif', true);
    setKargoFirmalar(data || []);
    const varsayilan = (data || []).find(f => f.varsayilan);
    if (varsayilan) setKargoForm(f => ({ ...f, kargo_firma_id: varsayilan.id }));
  }

  // ─── ÜRÜN EKLEME FONKSİYONLARI ───
  async function urunModalAc() {
    setUrunArama('');
    setEkSepet([]);
    setUrunHata('');

    // Temsilci ise sadece yetkili firmaların ürünleri
    let urunQuery = supabase.from('urunler').select('*, firmalar(ad)').eq('aktif', true).order('ad');
    if (profil?.rol === 'temsilci') {
      const { data: yetkiler } = await supabase.from('temsilci_firma_yetki').select('firma_id').eq('kullanici_id', profil.id);
      const yetkiliIds = (yetkiler || []).map(y => y.firma_id);
      if (yetkiliIds.length > 0) {
        urunQuery = urunQuery.in('firma_id', yetkiliIds);
      } else {
        setTumUrunler([]);
        setUrunModal(true);
        return;
      }
    }

    const [{ data: u }, { data: s }] = await Promise.all([
      urunQuery,
      siparis.depo_id
        ? supabase.from('depo_stok').select('urun_id, miktar').eq('depo_id', siparis.depo_id)
        : { data: [] },
    ]);

    const map = {};
    (s || []).forEach(st => { map[st.urun_id] = st.miktar; });
    setStokMap(map);
    setTumUrunler(u || []);
    setUrunModal(true);
  }

  const FIPGL23_ID = 'acb52946-e2a8-4fac-84db-04865b85f944';

  function birimFiyat(urun) {
    if (urun.firma_id === FIPGL23_ID) return urun.banka_fiyat_kdvsiz || 0;
    if (siparis.odeme_turu === 'kredi_karti') return urun.kk_fiyat_kdvli || 0;
    return urun.banka_fiyat_kdvsiz || 0;
  }

  function komisyonHesapla(urun, miktar) {
    if (urun.komisyonsuz) return 0;
    const fiyat = birimFiyat(urun);
    const oran = (urun.firma_id === FIPGL23_ID || siparis.musteri_tipi !== 'kurumsal')
      ? urun.bireysel_komisyon
      : urun.kurumsal_komisyon;
    return (fiyat * miktar) * ((oran || 0) / 100);
  }

  function ekSepeteEkle(urun) {
    const stok = stokMap[urun.id];
    if (siparis.depo_id && (stok === undefined || stok === 0)) {
      setUrunHata(`⛔ "${urun.ad}" ürününün depoda stoğu YOK.`);
      return;
    }
    setUrunHata('');
    const fiyat = birimFiyat(urun);
    const komisyon = komisyonHesapla(urun, 1);
    setEkSepet(prev => {
      const mevcut = prev.find(i => i.urun.id === urun.id);
      if (mevcut) return prev.map(i => i.urun.id === urun.id
        ? { ...i, miktar: i.miktar + 1, komisyon: komisyonHesapla(urun, i.miktar + 1) }
        : i);
      return [...prev, { urun, miktar: 1, fiyat, komisyon }];
    });
  }

  function ekSepetMiktarGuncelle(urunId, yeniMiktar) {
    const miktar = parseInt(yeniMiktar);
    if (isNaN(miktar) || miktar <= 0) {
      setEkSepet(prev => prev.filter(i => i.urun.id !== urunId));
      return;
    }
    setEkSepet(prev => prev.map(i => i.urun.id === urunId
      ? { ...i, miktar, komisyon: komisyonHesapla(i.urun, miktar) }
      : i));
  }

  async function ekUrunleriKaydet() {
    if (ekSepet.length === 0) return;
    setUrunYukleniyor(true);

    // Yeni kalemleri ekle
    const yeniKalemler = ekSepet.map(({ urun, miktar, fiyat, komisyon }) => ({
      siparis_id: id,
      urun_id: urun.id,
      miktar,
      birim_fiyat: fiyat,
      toplam: fiyat * miktar,
      komisyon_orani: siparis.musteri_tipi === 'kurumsal' ? urun.kurumsal_komisyon : urun.bireysel_komisyon,
      komisyon_tutari: komisyon,
    }));
    await supabase.from('siparis_kalemleri').insert(yeniKalemler);

    // Stoktan düş
    if (siparis.depo_id) {
      for (const { urun, miktar } of ekSepet) {
        const mevcutStok = stokMap[urun.id] || 0;
        const yeniStok = Math.max(0, mevcutStok - miktar);
        await supabase.from('depo_stok').upsert(
          { depo_id: siparis.depo_id, urun_id: urun.id, miktar: yeniStok },
          { onConflict: 'depo_id,urun_id' }
        );
        await supabase.from('stok_hareketleri').insert({
          depo_id: siparis.depo_id, urun_id: urun.id, hareket_tipi: 'cikis',
          miktar, onceki_stok: mevcutStok, sonraki_stok: yeniStok,
          aciklama: `Sipariş #${siparis.siparis_no} — ek ürün eklendi`,
        });
      }
    }

    // Sipariş toplamlarını güncelle (mevcut + eklenen)
    const mevcutTutar = kalemler.reduce((a, k) => a + (k.toplam || 0), 0);
    const mevcutKomisyon = kalemler.reduce((a, k) => a + (k.komisyon_tutari || 0), 0);
    const ekTutar = ekSepet.reduce((a, { fiyat, miktar }) => a + fiyat * miktar, 0);
    const ekKomisyon = ekSepet.reduce((a, { komisyon }) => a + komisyon, 0);

    await supabase.from('siparisler').update({
      toplam_tutar: mevcutTutar + ekTutar,
      toplam_komisyon: mevcutKomisyon + ekKomisyon,
    }).eq('id', id);

    setUrunModal(false);
    setUrunYukleniyor(false);
    fetchData();
  }

  // ─── MEVCUT FONKSİYONLAR ───
  async function durumGuncelle(yeniDurum) {
    await supabase.from('siparisler').update({ siparis_durumu: yeniDurum }).eq('id', id);
    fetchData();
  }

  async function kargoGuncelle() {
    const update = {};
    if (kargoForm.elden_teslim) {
      update.siparis_durumu = 'teslim';
      update.kargo_kodu = kargoForm.teslim_notu ? `Elden Teslim: ${kargoForm.teslim_notu}` : 'Elden Teslim';
      update.kargo_durumu = 'teslim_edildi';
      update.kargo_teslim_tarihi = new Date().toISOString();
    } else {
      update.kargo_kodu = kargoForm.kargo_kodu;
      update.siparis_durumu = 'kargoda';
      update.kargo_durumu = 'kargoda';
      update.kargo_cikis_tarihi = new Date().toISOString();
      if (kargoForm.kargo_firma_id) update.kargo_firma_id = kargoForm.kargo_firma_id;
    }
    await supabase.from('siparisler').update(update).eq('id', id);
    await supabase.from('kargo_loglari').insert({
      siparis_id: id,
      kargo_firma_id: kargoForm.kargo_firma_id || null,
      islem_yapan: profil?.id,
      takip_no: kargoForm.kargo_kodu || null,
      durum: kargoForm.elden_teslim ? 'elden_teslim' : 'kargoya_verildi',
    });
    setKargoModal(false);
    fetchData();
  }

  function whatsappLinkGonder() {
    if (!siparis) return;
    const odemeUrl = `${window.location.origin}/odeme/${siparis.siparis_no}`;
    const tel = siparis.musteriler?.telefon?.replace(/\D/g, '') || '';
    const mesaj = encodeURIComponent(
      `Merhaba ${siparis.musteriler?.ad || ''},\n\nSiparişiniz hazır. Bilgilerinizi onaylamak için lütfen aşağıdaki linki kullanınız:\n${odemeUrl}\n\nSipariş No: ${siparis.siparis_no}\nTutar: ${(siparis.toplam_tutar || 0).toLocaleString('tr-TR')} ₺\n\nTeşekkürler.`
    );
    const waUrl = tel ? `https://wa.me/90${tel.replace(/^0/, '')}?text=${mesaj}` : `https://wa.me/?text=${mesaj}`;
    window.open(waUrl, '_blank');
  }

  async function temsilciDekontYukle() {
    if (!dekontDosya) return;
    setYukleniyor(true);
    try {
      const dosyaAdi = `dekontlar/${siparis.siparis_no}_${Date.now()}.${dekontDosya.name.split('.').pop()}`;
      const { error: upErr } = await supabase.storage.from('dekontlar').upload(dosyaAdi, dekontDosya, { upsert: true });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from('dekontlar').getPublicUrl(dosyaAdi);
      await supabase.from('siparisler').update({ odeme_durumu: 'odendi' }).eq('id', id);
      await supabase.from('odeme_dekontlari').insert({
        siparis_id: id,
        tutar: siparis.toplam_tutar,
        odeme_turu: odemeForm.odeme_turu || 'banka',
        not_: `Sipariş No: ${siparis.siparis_no} — Temsilci tarafından yüklendi`,
        dekont_url: urlData?.publicUrl,
        created_by: profil?.id,
      });
      setOdemeModal(false);
      setDekontDosya(null);
      fetchData();
    } catch (err) {
      alert('Hata: ' + err.message);
    }
    setYukleniyor(false);
  }

  async function nakitOdemeKaydet() {
    setYukleniyor(true);
    try {
      let dekontUrl = null;
      if (dekontDosya) {
        const dosyaAdi = `dekontlar/${siparis.siparis_no}_nakit_${Date.now()}.${dekontDosya.name.split('.').pop()}`;
        const { error: upErr } = await supabase.storage.from('dekontlar').upload(dosyaAdi, dekontDosya, { upsert: true });
        if (upErr) throw upErr;
        const { data: urlData } = supabase.storage.from('dekontlar').getPublicUrl(dosyaAdi);
        dekontUrl = urlData?.publicUrl;
      }
      await supabase.from('siparisler').update({ odeme_durumu: 'odendi' }).eq('id', id);
      await supabase.from('odeme_dekontlari').insert({
        siparis_id: id,
        tutar: parseFloat(odemeForm.tutar) || siparis.toplam_tutar,
        odeme_turu: 'elden',
        not_: `Ödeme Elden Alınmıştır. Sipariş No: ${siparis.siparis_no}${odemeForm.not_ ? ' — ' + odemeForm.not_ : ''}`,
        dekont_url: dekontUrl,
        created_by: profil?.id,
      });
      setOdemeModal(false);
      setDekontDosya(null);
      fetchData();
    } catch (err) {
      alert('Hata: ' + err.message);
    }
    setYukleniyor(false);
  }

  async function faturaDurumuGuncelle() {
    await supabase.from('siparisler').update({ fatura_durumu: !siparis.fatura_durumu }).eq('id', id);
    fetchData();
  }

  async function handleSil() {
    if (!window.confirm('Bu siparişi silmek istediğinizden emin misiniz?')) return;
    await supabase.from('siparisler').delete().eq('id', id);
    navigate('/siparisler');
  }

  async function takipNoSorgula() {
    setTakipSorguluyor(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/kargo-sorgula`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ siparis_id: id }),
      });
      const result = await res.json();
      if (result.basarili) {
        fetchData();
        alert(`✅ Takip No güncellendi!\nTakip No: ${result.takip_no}`);
      } else {
        alert('❌ ' + result.mesaj);
      }
    } catch (err) {
      alert('Hata: ' + err.message);
    }
    setTakipSorguluyor(false);
  }

  const durumlar = ['alinidi', 'hazirlaniyor', 'kargoda', 'teslim', 'iade', 'iptal'];
  const durumRenk = { alinidi: 'badge-blue', hazirlaniyor: 'badge-yellow', kargoda: 'badge-orange', teslim: 'badge-green', iade: 'badge-red', iptal: 'badge-gray' };
  const isTemsilci = profil?.rol === 'temsilci';

  if (loading) return <div className="loading">Yükleniyor...</div>;
  if (!siparis) return <div className="empty">Sipariş bulunamadı</div>;

  const siparisLinki = `${window.location.origin}/odeme/${siparis.siparis_no}`;

  // Ürün modal filtrelenmiş liste
  const filtreliUrunler = tumUrunler.filter(u =>
    !urunArama || u.ad.toLowerCase().includes(urunArama.toLowerCase())
  );
  const altGruplar = [...new Set(filtreliUrunler.map(u => (u.alt_grup || 'Diğer').trim()))].sort();

  return (
    <div>
      <div className="page-header">
        <div>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/siparisler')} style={{ marginBottom: 8 }}>← Siparişler</button>
          <h1 className="page-title mono">#{siparis.siparis_no}</h1>
          <p className="page-subtitle">{new Date(siparis.created_at).toLocaleString('tr-TR')} • {siparis.firmalar?.ad}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {siparis.odeme_durumu !== 'odendi' && (
            <button className="btn btn-ghost btn-sm" onClick={whatsappLinkGonder} style={{ color: '#25D366', borderColor: '#25D366' }}>
              📱 WhatsApp Sipariş Linki
            </button>
          )}
          {/* YENİ: ÜRÜN EKLE BUTONU */}
          {siparis.siparis_durumu !== 'iptal' && (
            <button className="btn btn-ghost btn-sm" onClick={urunModalAc} style={{ color: 'var(--accent)', borderColor: 'var(--accent)' }}>
              ➕ Ürün Ekle
            </button>
          )}
          {(profil?.rol === 'admin' || profil?.rol === 'super_admin' || profil?.rol === 'depocu') &&
            siparis.siparis_durumu !== 'teslim' && siparis.siparis_durumu !== 'iptal' && (
            <button className="btn btn-primary btn-sm" onClick={() => {
              setKargoForm({ kargo_kodu: siparis.kargo_kodu || '', elden_teslim: false, teslim_notu: '' });
              setKargoModal(true);
            }}>🚚 Kargo / Teslim</button>
          )}
          {siparis.siparis_durumu === 'kargoda' && siparis.kargo_firma_id && (
            <button className="btn btn-ghost btn-sm" style={{ color: 'var(--accent)', borderColor: 'var(--accent)' }}
              onClick={takipNoSorgula} disabled={takipSorguluyor}>
              {takipSorguluyor ? '⏳ Sorgulanıyor...' : '🔍 Takip No Sorgula'}
            </button>
          )}
          {!isTemsilci && (
            <button className="btn btn-ghost btn-sm" onClick={faturaDurumuGuncelle}>
              {siparis.fatura_durumu ? '✓ Fatura Kesildi' : '○ Fatura Kesilmedi'}
            </button>
          )}
          {(profil?.rol === 'admin' || profil?.rol === 'super_admin') && (
            <button className="btn btn-danger btn-sm" onClick={handleSil}>🗑 Sil</button>
          )}
          {siparis.odeme_durumu !== 'odendi' && (
            <button className="btn btn-primary btn-sm" onClick={() => {
              setOdemeForm({ tutar: siparis.toplam_tutar, odeme_turu: 'banka', not_: '' });
              setOdemeSekmesi('temsilci_yukle');
              setOdemeModal(true);
            }}>💳 Ödeme Al</button>
          )}
        </div>
      </div>

      {/* DURUM GÜNCELLEME */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ color: 'var(--text2)', fontSize: 13, marginRight: 4 }}>Sipariş Durumu:</span>
          {durumlar.map(d => (
            <button key={d} onClick={() => durumGuncelle(d)}
              className={`badge ${siparis.siparis_durumu === d ? durumRenk[d] : 'badge-gray'}`}
              style={{ border: 'none', cursor: 'pointer', padding: '6px 12px', fontSize: 12 }}>
              {d}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20 }}>
        <div>
          {/* SİPARİŞ KALEMLERİ */}
          <div className="card" style={{ marginBottom: 16, padding: 0 }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700 }}>Sipariş Kalemleri</span>
              {siparis.siparis_durumu !== 'iptal' && (
                <button className="btn btn-ghost btn-sm" onClick={urunModalAc} style={{ color: 'var(--accent)' }}>
                  ➕ Ürün Ekle
                </button>
              )}
            </div>
            <table className="table">
              <thead>
                <tr><th>Ürün</th><th>Miktar</th><th>Birim Fiyat</th><th>Kom. %</th><th>Komisyon</th><th>Toplam</th></tr>
              </thead>
              <tbody>
                {kalemler.map(k => (
                  <tr key={k.id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{k.urunler?.ad}</div>
                      <div style={{ fontSize: 11, color: 'var(--text2)' }}>{k.urunler?.alt_grup || k.urunler?.grup}</div>
                    </td>
                    <td style={{ fontWeight: 600 }}>{k.miktar}</td>
                    <td>{(k.birim_fiyat || 0).toLocaleString('tr-TR')} ₺</td>
                    <td style={{ color: 'var(--text2)' }}>%{k.komisyon_orani || 0}</td>
                    <td style={{ color: 'var(--yellow)' }}>{(k.komisyon_tutari || 0).toLocaleString('tr-TR')} ₺</td>
                    <td style={{ fontWeight: 700 }}>{(k.toplam || 0).toLocaleString('tr-TR')} ₺</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* MÜŞTERİ BİLGİLERİ */}
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 style={{ marginBottom: 12, fontWeight: 700 }}>Müşteri & Teslimat</h3>
            <div className="form-row cols-2">
              {[
                ['Müşteri', siparis.musteriler?.ad],
                ['Telefon', siparis.musteriler?.telefon],
                ['Şehir', siparis.musteriler?.sehir],
                ['Temsilci', siparis.kullanicilar ? `${siparis.kullanicilar.ad} ${siparis.kullanicilar.soyad}` : '-'],
                ['Depo', siparis.depolar?.ad],
                ['Ödeme Türü', siparis.odeme_turu === 'banka' ? 'Banka Havalesi' : siparis.odeme_turu === 'kredi_karti' ? 'Kredi Kartı' : 'Elden/Nakit'],
                ['KDV', siparis.kdv_durumu ? "KDV'li" : "KDV'siz"],
                ['Kargo Takip No', siparis.kargo_kodu || '-'],
              ].map(([k, v]) => (
                <div key={k} style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 2 }}>{k}</div>
                  <div style={{ fontWeight: 500 }}>{v || '-'}</div>
                </div>
              ))}
            </div>
            {siparis.adres && <div style={{ marginTop: 8, padding: 10, background: 'var(--bg3)', borderRadius: 'var(--r)', fontSize: 13, color: 'var(--text2)' }}>{siparis.adres}</div>}
            {siparis.not_ && <div style={{ marginTop: 8, padding: 10, background: 'rgba(234,179,8,0.1)', borderRadius: 'var(--r)', fontSize: 13, color: 'var(--yellow)' }}>Not: {siparis.not_}</div>}

            {siparis.odeme_durumu !== 'odendi' && (
              <div style={{ marginTop: 16, padding: 12, background: 'rgba(37,211,102,0.08)', border: '1px solid rgba(37,211,102,0.3)', borderRadius: 'var(--r)' }}>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>📎 Sipariş Linki</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <code style={{ fontSize: 12, color: '#25D366', flex: 1, wordBreak: 'break-all' }}>{siparisLinki}</code>
                  <button className="btn btn-ghost btn-sm" onClick={() => navigator.clipboard.writeText(siparisLinki)}>Kopyala</button>
                  <button className="btn btn-ghost btn-sm" style={{ color: '#25D366', borderColor: '#25D366' }} onClick={whatsappLinkGonder}>📱 Gönder</button>
                </div>
              </div>
            )}
          </div>

          {/* YÜKLENEN DEKONTLAR */}
          {dekontlar.length > 0 && (
            <div className="card">
              <h3 style={{ marginBottom: 12, fontWeight: 700 }}>Ödeme Dekontları</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {dekontlar.map(d => (
                  <div key={d.id} style={{ padding: 12, background: 'var(--bg3)', borderRadius: 'var(--r)', border: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontWeight: 600, color: 'var(--green)' }}>{(d.tutar || 0).toLocaleString('tr-TR')} ₺</span>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span style={{ fontSize: 12, color: 'var(--text2)' }}>{new Date(d.created_at).toLocaleString('tr-TR')}</span>
                        {!isTemsilci && (
                          <button className="btn btn-danger btn-sm" style={{ padding: '2px 8px', fontSize: 11 }}
                            onClick={async () => {
                              if (!window.confirm('Bu dekontu silmek istediğinizden emin misiniz?')) return;
                              await supabase.from('odeme_dekontlari').delete().eq('id', d.id);
                              fetchData();
                            }}>Sil</button>
                        )}
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: d.dekont_url ? 8 : 0 }}>
                      {d.odeme_turu === 'banka' ? '🏦 Banka Havalesi' : d.odeme_turu === 'elden' ? '💵 Elden/Nakit' : '💳 Kredi Kartı'}
                      {d.not_ && ` • ${d.not_}`}
                    </div>
                    {d.dekont_url && (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <a href={d.dekont_url} target="_blank" rel="noreferrer"
                          style={{ fontSize: 12, color: 'var(--accent)', textDecoration: 'none' }}>
                          📎 Dekontu Görüntüle
                        </a>
                        {d.dekont_url.match(/\.(jpg|jpeg|png|webp)$/i) && (
                          <img src={d.dekont_url} alt="dekont" style={{ height: 60, borderRadius: 4, border: '1px solid var(--border)', cursor: 'pointer' }}
                            onClick={() => window.open(d.dekont_url, '_blank')} />
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* SAĞ: ÖZET */}
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 style={{ marginBottom: 16, fontWeight: 700 }}>Finansal Özet</h3>
            {[
              ['Toplam Tutar', siparis.toplam_tutar, 'var(--text)', true],
              ...(!isTemsilci ? [['Maliyet', siparis.toplam_maliyet, 'var(--text2)', false]] : []),
              ['Komisyon', siparis.toplam_komisyon, 'var(--yellow)', false],
              ...(!isTemsilci ? [['Net Kar', siparis.net_kar, 'var(--green)', true]] : []),
            ].map(([k, v, renk, bold]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ color: 'var(--text2)', fontSize: 13 }}>{k}</span>
                <span style={{ color: renk, fontWeight: bold ? 700 : 400 }}>{(v || 0).toLocaleString('tr-TR')} ₺</span>
              </div>
            ))}
          </div>

          <div className="card">
            <h3 style={{ marginBottom: 12, fontWeight: 700 }}>Durum</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text2)', fontSize: 13 }}>Sipariş</span>
                <span className={`badge ${durumRenk[siparis.siparis_durumu]}`}>{siparis.siparis_durumu}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text2)', fontSize: 13 }}>Ödeme</span>
                <span className={`badge ${siparis.odeme_durumu === 'odendi' ? 'badge-green' : 'badge-red'}`}>{siparis.odeme_durumu}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text2)', fontSize: 13 }}>Fatura</span>
                <span className={`badge ${siparis.fatura_durumu ? 'badge-green' : 'badge-red'}`}>{siparis.fatura_durumu ? 'Kesildi' : 'Kesilmedi'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── ÜRÜN EKLEME MODAL ─── */}
      {urunModal && (
        <div className="modal-overlay" onClick={() => setUrunModal(false)}>
          <div className="modal" style={{ maxWidth: 700, maxHeight: '90vh', display: 'flex', flexDirection: 'column', width: '95vw' }} onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">➕ Siparişe Ürün Ekle — #{siparis.siparis_no}</h3>

            {ekSepet.length > 0 && (
              <div style={{ background: 'rgba(224,123,0,0.08)', border: '1px solid var(--accent)', borderRadius: 'var(--r)', padding: '8px 12px', marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>🛒 {ekSepet.length} ürün</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--green)' }}>{ekSepet.reduce((a, { fiyat, miktar }) => a + fiyat * miktar, 0).toLocaleString('tr-TR')} ₺</span>
                </div>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {ekSepet.map(({ urun, miktar }) => (
                    <div key={urun.id} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 4, padding: '2px 8px', fontSize: 12 }}>
                      <span>{urun.ad} ×{miktar}</span>
                      <button style={{ border: 'none', background: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 12, padding: 0 }}
                        onClick={() => ekSepetMiktarGuncelle(urun.id, 0)}>✕</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <input className="input" placeholder="Ürün ara..." value={urunArama}
              onChange={e => setUrunArama(e.target.value)} style={{ marginBottom: 10 }} autoFocus />

            {urunHata && (
              <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--r)', padding: '8px 12px', color: 'var(--red)', fontSize: 13, marginBottom: 8 }}>
                {urunHata}
              </div>
            )}

            <div style={{ overflow: 'auto', flex: 1 }}>
              {altGruplar.map(grup => {
                const grupUrunleri = filtreliUrunler.filter(u => (u.alt_grup || 'Diğer').trim() === grup);
                return (
                  <div key={grup} style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
                      📂 {grup}
                    </div>
                    {grupUrunleri.map(u => {
                      const stok = stokMap[u.id];
                      const stokYok = siparis.depo_id && (stok === undefined || stok === 0);
                      const sepette = ekSepet.find(i => i.urun.id === u.id);
                      return (
                        <div key={u.id} style={{
                          padding: '8px 10px', borderRadius: 'var(--r)', marginBottom: 4,
                          background: sepette ? 'rgba(224,123,0,0.08)' : 'var(--bg3)',
                          border: `1px solid ${sepette ? 'var(--accent)' : 'var(--border)'}`,
                          opacity: stokYok ? 0.4 : 1,
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 13, fontWeight: 500 }}>{u.ad}</div>
                              <div style={{ fontSize: 11, color: 'var(--text2)' }}>
                                {u.firmalar?.ad} • {birimFiyat(u).toLocaleString('tr-TR')} ₺
                                {siparis.depo_id && <span style={{ marginLeft: 8 }}>Stok: {stok ?? 0}</span>}
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 8 }}>
                              {sepette && (
                                <>
                                  <button className="btn btn-ghost btn-sm" style={{ padding: '4px 10px' }}
                                    onClick={() => ekSepetMiktarGuncelle(u.id, sepette.miktar - 1)}>−</button>
                                  <span style={{ minWidth: 24, textAlign: 'center', fontWeight: 700 }}>{sepette.miktar}</span>
                                </>
                              )}
                              <button className="btn btn-primary btn-sm" onClick={() => ekSepeteEkle(u)}
                                disabled={stokYok} style={{ minWidth: 60 }}>
                                {sepette ? '+1' : '+ Ekle'}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
              {filtreliUrunler.length === 0 && (
                <div style={{ textAlign: 'center', padding: 30, color: 'var(--text2)' }}>Ürün bulunamadı</div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
              <button className="btn btn-ghost" onClick={() => setUrunModal(false)}>İptal</button>
              <button className="btn btn-primary" onClick={ekUrunleriKaydet}
                disabled={ekSepet.length === 0 || urunYukleniyor}>
                {urunYukleniyor ? 'Kaydediliyor...' : `✓ ${ekSepet.length} Ürün Ekle`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* KARGO MODAL */}
      {kargoModal && (
        <div className="modal-overlay" onClick={() => setKargoModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Kargo / Teslim Bilgisi — #{siparis.siparis_no}</h3>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <button className={`btn ${!kargoForm.elden_teslim ? 'btn-primary' : 'btn-ghost'} btn-sm`}
                onClick={() => setKargoForm(f => ({ ...f, elden_teslim: false }))}>🚚 Kargo ile Gönderim</button>
              <button className={`btn ${kargoForm.elden_teslim ? 'btn-primary' : 'btn-ghost'} btn-sm`}
                onClick={() => setKargoForm(f => ({ ...f, elden_teslim: true }))}>🤝 Elden Teslim</button>
            </div>
            {!kargoForm.elden_teslim ? (
              <>
                <div className="form-group">
                  <label className="label">Kargo Firması</label>
                  <select className="select" value={kargoForm.kargo_firma_id}
                    onChange={e => setKargoForm(f => ({ ...f, kargo_firma_id: e.target.value }))}>
                    <option value="">Seçin...</option>
                    {kargoFirmalar.map(f => (
                      <option key={f.id} value={f.id}>{f.ad}{f.varsayilan ? ' (Varsayılan)' : ''}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="label">Kargo Takip No</label>
                  <input className="input" value={kargoForm.kargo_kodu}
                    onChange={e => setKargoForm(f => ({ ...f, kargo_kodu: e.target.value }))}
                    placeholder="Kargo takip numarası..." />
                </div>
              </>
            ) : (
              <div className="form-group">
                <label className="label">Teslim Notu</label>
                <input className="input" value={kargoForm.teslim_notu}
                  onChange={e => setKargoForm(f => ({ ...f, teslim_notu: e.target.value }))}
                  placeholder="İstanbul Avrupa Depo / Ahmet Bey..." autoFocus />
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-ghost" onClick={() => setKargoModal(false)}>İptal</button>
              {!kargoForm.elden_teslim && kargoFirmalar.find(f => f.id === kargoForm.kargo_firma_id)?.api_url && kargoFirmalar.find(f => f.id === kargoForm.kargo_firma_id)?.kullanici_adi && (
                <button className="btn btn-ghost" style={{ color: 'var(--accent)', borderColor: 'var(--accent)' }}
                  onClick={async () => {
                    setYukleniyor(true);
                    try {
                      const { data: { session } } = await supabase.auth.getSession();
                      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/kargo-olustur`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${session?.access_token}`,
                          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
                        },
                        body: JSON.stringify({ siparis_id: id, kargo_firma_id: kargoForm.kargo_firma_id }),
                      });
                      const result = await res.json();
                      if (result.basarili) {
                        setKargoModal(false);
                        fetchData();
                        alert(`✅ Kargo oluşturuldu!\nTakip No: ${result.barkod}`);
                      } else {
                        alert('❌ ' + result.mesaj);
                      }
                    } catch (err) {
                      alert('Hata: ' + err.message);
                    }
                    setYukleniyor(false);
                  }}>
                  {yukleniyor ? '⏳ Oluşturuluyor...' : '🚀 Otomatik Kargo Oluştur'}
                </button>
              )}
              <button className="btn btn-primary" onClick={kargoGuncelle}>
                {kargoForm.elden_teslim ? '✓ Teslim Edildi Olarak İşaretle' : '✓ Manuel Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ÖDEME MODAL */}
      {odemeModal && (
        <div className="modal-overlay" onClick={() => setOdemeModal(false)}>
          <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">💳 Ödeme Al — #{siparis.siparis_no}</h3>
            <p style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 16 }}>
              Tutar: <strong style={{ color: 'var(--text)' }}>{(siparis.toplam_tutar || 0).toLocaleString('tr-TR')} ₺</strong>
            </p>

            <div style={{ display: 'flex', gap: 6, marginBottom: 20, borderBottom: '1px solid var(--border)', paddingBottom: 12 }}>
              {[
                { id: 'temsilci_yukle', label: '📎 Dekont Yükle' },
                { id: 'nakit', label: '💵 Nakit / Elden' },
              ].map(s => (
                <button key={s.id}
                  className={`btn btn-sm ${odemeSekmesi === s.id ? 'btn-primary' : 'btn-ghost'}`}
                  style={{ flex: 1, justifyContent: 'center', fontSize: 13 }}
                  onClick={() => { setOdemeSekmesi(s.id); setDekontDosya(null); }}>
                  {s.label}
                </button>
              ))}
            </div>

            {odemeSekmesi === 'temsilci_yukle' && (
              <div>
                <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>Müşteri WhatsApp'tan dekontu gönderdi. Aşağıdan yükleyin.</p>
                <div className="form-group">
                  <label className="label">Ödeme Türü</label>
                  <select className="select" value={odemeForm.odeme_turu}
                    onChange={e => setOdemeForm(f => ({ ...f, odeme_turu: e.target.value }))}>
                    <option value="banka">Banka Havalesi</option>
                    <option value="kredi_karti">Kredi Kartı</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="label">Dekont / Makbuz (JPG, PNG, PDF)</label>
                  <div style={{ border: `2px dashed ${dekontDosya ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 'var(--r)', padding: '24px', textAlign: 'center', cursor: 'pointer', background: dekontDosya ? 'rgba(224,123,0,0.06)' : 'var(--bg3)' }}
                    onClick={() => dosyaRef.current?.click()}
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setDekontDosya(f); }}>
                    {dekontDosya ? (
                      <div><div style={{ fontSize: 24, marginBottom: 4 }}>✓</div><div style={{ fontWeight: 600 }}>{dekontDosya.name}</div><div style={{ fontSize: 12, color: 'var(--text2)' }}>{(dekontDosya.size / 1024).toFixed(0)} KB</div></div>
                    ) : (
                      <div style={{ color: 'var(--text2)', fontSize: 13 }}><div style={{ fontSize: 28, marginBottom: 6 }}>📎</div>Dosyayı buraya sürükle veya tıkla</div>
                    )}
                  </div>
                  <input ref={dosyaRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }}
                    onChange={e => setDekontDosya(e.target.files[0])} />
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button className="btn btn-ghost" onClick={() => setOdemeModal(false)}>İptal</button>
                  <button className="btn btn-primary" onClick={temsilciDekontYukle} disabled={!dekontDosya || yukleniyor}>
                    {yukleniyor ? 'Yükleniyor...' : '✓ Ödeme Alındı'}
                  </button>
                </div>
              </div>
            )}

            {odemeSekmesi === 'nakit' && (
              <div>
                <div className="alert alert-warning" style={{ marginBottom: 16 }}>
                  ⚠️ "Ödeme Elden Alınmıştır — Sipariş No: {siparis.siparis_no}" olarak kaydedilecek.
                </div>
                <div className="form-group">
                  <label className="label">Alınan Tutar (₺)</label>
                  <input className="input" type="number" value={odemeForm.tutar}
                    onChange={e => setOdemeForm(f => ({ ...f, tutar: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="label">Nakit Makbuzu / Fotoğraf (isteğe bağlı)</label>
                  <div style={{ border: `2px dashed ${dekontDosya ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 'var(--r)', padding: '24px', textAlign: 'center', cursor: 'pointer', background: dekontDosya ? 'rgba(224,123,0,0.06)' : 'var(--bg3)' }}
                    onClick={() => dosyaRef.current?.click()}
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setDekontDosya(f); }}>
                    {dekontDosya ? (
                      <div><div style={{ fontSize: 24, marginBottom: 4 }}>✓</div><div style={{ fontWeight: 600 }}>{dekontDosya.name}</div><div style={{ fontSize: 12, color: 'var(--text2)' }}>{(dekontDosya.size / 1024).toFixed(0)} KB</div></div>
                    ) : (
                      <div style={{ color: 'var(--text2)', fontSize: 13 }}><div style={{ fontSize: 28, marginBottom: 6 }}>📸</div>Makbuz fotoğrafı (isteğe bağlı)</div>
                    )}
                  </div>
                  <input ref={dosyaRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }}
                    onChange={e => setDekontDosya(e.target.files[0])} />
                </div>
                <div className="form-group">
                  <label className="label">Ek Not (isteğe bağlı)</label>
                  <input className="input" value={odemeForm.not_}
                    onChange={e => setOdemeForm(f => ({ ...f, not_: e.target.value }))} placeholder="Ek not..." />
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button className="btn btn-ghost" onClick={() => setOdemeModal(false)}>İptal</button>
                  <button className="btn btn-primary" onClick={nakitOdemeKaydet} disabled={yukleniyor}>
                    {yukleniyor ? 'Kaydediliyor...' : '✓ Nakit Ödeme Alındı'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
